import React, { useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogContent,
  Button,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Fullscreen,
  FullscreenExit,
  OpenInNew,
  PictureAsPdf,
  FileOpen,
} from "@mui/icons-material";
import { useLanguage } from "../../contexts/LanguageContext";

const C = {
  pdf:       "#EF4444",
  pdfBg:     "#FEF2F2",
  border:    "#E5E7EB",
  white:     "#FFFFFF",
  bg:        "#F8F9FA",
  text:      "#1F2937",
  muted:     "#6B7280",
  shadow:    "rgba(0,0,0,0.07)",
  blue:      "#1565C0",
  blueBg:    "#EEF4FF",
};

interface PDFViewerProps {
  url: string;
  downloadUrl?: string;
  title?: string;
  onDownload?: () => void;
}

// ── Mobile tap-to-open card ────────────────────────────────────────────────
const MobilePDFCard: React.FC<{ url: string; title: string }> = ({ url, title }) => {
  const { t } = useLanguage();
  const tv = t.courseDetail.viewer;
  return (
  <Box
    sx={{
      borderRadius: "16px",
      border: `1px solid ${C.border}`,
      bgcolor: C.white,
      boxShadow: `0 2px 14px ${C.shadow}`,
      overflow: "hidden",
    }}
  >
    {/* Header strip */}
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        px: 2,
        py: 1.5,
        borderBottom: `1px solid ${C.border}`,
        bgcolor: C.bg,
      }}
    >
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          bgcolor: C.pdfBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <PictureAsPdf sx={{ color: C.pdf, fontSize: 20 }} />
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          fontWeight={700}
          fontSize="0.9rem"
          noWrap
          sx={{ color: C.text }}
        >
          {title}
        </Typography>
        <Typography variant="caption" sx={{ color: C.muted }}>
          {tv.pdfDocument}
        </Typography>
      </Box>
      <Chip
        label="PDF"
        size="small"
        sx={{
          height: 20,
          fontSize: "0.65rem",
          bgcolor: C.pdfBg,
          color: C.pdf,
          fontWeight: 700,
          border: `1px solid ${C.pdf}22`,
          flexShrink: 0,
        }}
      />
    </Box>

    {/* Tap area */}
    <Box
      sx={{
        px: 2,
        py: 4,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 2,
        textAlign: "center",
      }}
    >
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "20px",
          bgcolor: C.pdfBg,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <PictureAsPdf sx={{ color: C.pdf, fontSize: 38 }} />
      </Box>

      <Box>
        <Typography fontWeight={700} fontSize="1rem" sx={{ color: C.text, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ color: C.muted }}>
          {tv.pdfMobileHint}
        </Typography>
      </Box>

      <Button
        variant="contained"
        size="large"
        startIcon={<FileOpen />}
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          textTransform: "none",
          fontWeight: 700,
          borderRadius: "50px",
          px: 4,
          py: 1.4,
          fontSize: "0.95rem",
          bgcolor: C.blue,
          boxShadow: "0 4px 16px rgba(21,101,192,0.3)",
          "&:hover": { bgcolor: "#0D47A1" },
        }}
      >
        {tv.pdfOpenButton}
      </Button>

      <Typography variant="caption" sx={{ color: C.muted }}>
        {tv.pdfMobileFooter}
      </Typography>
    </Box>
  </Box>
  );
};

// ── Desktop iframe viewer ──────────────────────────────────────────────────
export const PDFViewer: React.FC<PDFViewerProps> = ({ url, title = "Document" }) => {
  const { t } = useLanguage();
  const tv = t.courseDetail.viewer;
  const [fullscreen, setFullscreen] = useState(false);
  const [loaded, setLoaded]         = useState(false);
  const [error, setError]           = useState(false);
  const theme                       = useTheme();
  const isMobile                    = useMediaQuery(theme.breakpoints.down("md"));

  // On mobile, skip the iframe entirely — use the native PDF viewer via a link
  if (isMobile) {
    return <MobilePDFCard url={url} title={title} />;
  }

  const renderIframe = () => (
    <Box sx={{ position: "relative", width: "100%", height: "100%", bgcolor: C.bg }}>
      {/* Loading shimmer */}
      {!loaded && !error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: C.bg,
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: "14px",
              bgcolor: C.pdfBg,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              animation: "pdfPulse 1.6s ease-in-out infinite",
              "@keyframes pdfPulse": {
                "0%,100%": { opacity: 1 },
                "50%":     { opacity: 0.4 },
              },
            }}
          >
            <PictureAsPdf sx={{ color: C.pdf, fontSize: 28 }} />
          </Box>
          <Typography variant="body2" sx={{ color: C.muted, fontWeight: 500 }}>
            {tv.pdfLoading}
          </Typography>
        </Box>
      )}

      {/* Error state */}
      {error && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 1.5,
            bgcolor: C.bg,
          }}
        >
          <PictureAsPdf sx={{ fontSize: 48, color: C.pdf, opacity: 0.25 }} />
          <Typography variant="body2" sx={{ color: C.muted }}>
            {tv.pdfInlineUnavailable}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<OpenInNew />}
            component="a"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              textTransform: "none",
              borderRadius: "50px",
              borderColor: C.blue,
              color: C.blue,
              "&:hover": { bgcolor: C.blueBg },
            }}
          >
            {tv.pdfOpenNewTab}
          </Button>
        </Box>
      )}

      <iframe
        src={url}
        title={title}
        onLoad={() => setLoaded(true)}
        onError={() => { setLoaded(true); setError(true); }}
        style={{
          width: "100%",
          height: "100%",
          border: "none",
          display: error ? "none" : "block",
          opacity: loaded ? 1 : 0,
          transition: "opacity 0.35s ease",
        }}
      />
    </Box>
  );

  return (
    <>
      {/* ── Inline card ── */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          borderRadius: "14px",
          border: `1px solid ${C.border}`,
          overflow: "hidden",
          boxShadow: `0 2px 14px ${C.shadow}`,
          bgcolor: C.white,
          height: "100%",
          minHeight: 560,
        }}
      >
        {/* Toolbar */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${C.border}`,
            bgcolor: C.white,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "8px",
                bgcolor: C.pdfBg,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <PictureAsPdf sx={{ color: C.pdf, fontSize: 17 }} />
            </Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              noWrap
              sx={{ color: C.text, maxWidth: 320 }}
            >
              {title}
            </Typography>
            <Chip
              label="PDF"
              size="small"
              sx={{
                height: 20,
                fontSize: "0.65rem",
                bgcolor: C.pdfBg,
                color: C.pdf,
                fontWeight: 700,
                border: `1px solid ${C.pdf}22`,
                flexShrink: 0,
              }}
            />
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25, flexShrink: 0 }}>
            <Tooltip title={tv.pdfOpenNewTab} arrow>
              <IconButton
                size="small"
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ color: C.muted, "&:hover": { color: C.blue } }}
              >
                <OpenInNew sx={{ fontSize: 17 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={tv.pdfFullscreen} arrow>
              <IconButton
                size="small"
                onClick={() => setFullscreen(true)}
                sx={{ color: C.muted, "&:hover": { color: C.blue } }}
              >
                <Fullscreen sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* PDF area */}
        <Box sx={{ flex: 1, minHeight: 0 }}>{renderIframe()}</Box>
      </Box>

      {/* ── Fullscreen dialog ── */}
      <Dialog
        open={fullscreen}
        onClose={() => setFullscreen(false)}
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "94vw",
            height: "94vh",
            maxWidth: "none",
            borderRadius: "16px",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <DialogContent
          sx={{
            p: 0,
            display: "flex",
            flexDirection: "column",
            height: "100%",
            overflow: "hidden",
          }}
        >
          {/* Dialog toolbar */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.5,
              py: 1.5,
              borderBottom: `1px solid ${C.border}`,
              bgcolor: C.white,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: C.pdfBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <PictureAsPdf sx={{ color: C.pdf, fontSize: 19 }} />
              </Box>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: C.text, lineHeight: 1.25 }}>
                  {title}
                </Typography>
                <Typography variant="caption" sx={{ color: C.muted }}>
                  PDF Document
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title={tv.pdfOpenNewTab} arrow>
                <IconButton
                  size="small"
                  component="a"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: C.muted, "&:hover": { color: C.blue } }}
                >
                  <OpenInNew sx={{ fontSize: 18 }} />
                </IconButton>
              </Tooltip>
              <Tooltip title={tv.pdfExitFullscreen} arrow>
                <IconButton
                  onClick={() => setFullscreen(false)}
                  sx={{ color: C.muted, "&:hover": { color: C.text } }}
                >
                  <FullscreenExit sx={{ fontSize: 22 }} />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>{renderIframe()}</Box>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PDFViewer;
